<template>
  <PageLayout title="Macros">
    <template #actions>
      <button class="btn btn-primary btn-sm" @click="openCreate">
        <i class="fas fa-plus mr-1"></i> New Macro
      </button>
    </template>

    <ContentCard>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <p class="text-muted mb-0">Sequence command yang bisa dijalankan dari halaman ini atau Macro widget.</p>
      </div>

      <div v-if="!generalMacros.length" class="text-center text-muted py-5">
        <i class="fas fa-play-circle fa-2x mb-3 d-block"></i>
        <p>Belum ada macro.</p>
        <button class="btn btn-outline-primary btn-sm" @click="openCreate">Buat macro pertama</button>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Steps</th>
              <th>Stop on error</th>
              <th class="text-right" style="width:180px">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in generalMacros" :key="m.id">
              <td>
                <span
                  class="macro-swatch mr-2"
                  :style="{ background: m.color, color: previewText(m.color) }"
                >
                  <i class="fas" :class="'fa-' + (m.icon || 'play')"></i>
                </span>
                <strong>{{ m.name }}</strong>
              </td>
              <td>
                <span class="badge badge-light border">{{ m.steps.length }}</span>
              </td>
              <td>
                <span v-if="m.stopOnError" class="text-muted">Yes</span>
                <span v-else class="text-muted">No</span>
              </td>
              <td class="text-right text-nowrap">
                <button
                  class="btn btn-sm btn-outline-primary mr-1"
                  title="Run"
                  :disabled="runningId === m.id"
                  @click="run(m)"
                >
                  <i class="fas" :class="runningId === m.id ? 'fa-spinner fa-spin' : 'fa-play'"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary mr-1" title="Edit" @click="openEdit(m)">
                  <i class="fas fa-pen"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" title="Delete" @click="confirmDelete(m)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="runStatus" class="small mt-3 mb-0" :class="runOk ? 'text-success' : 'text-danger'">{{ runStatus }}</p>
    </ContentCard>

    <modal name="edit-macro" height="auto" :width="640" :adaptive="true" :scrollable="true">
      <div class="p-4 macro-modal">
        <h5 class="mb-3">{{ form.id ? 'Edit Macro' : 'New Macro' }}</h5>

        <div class="form-group">
          <label class="small text-muted">Name</label>
          <input v-model="form.name" class="form-control" maxlength="100" placeholder="e.g. Go Live">
        </div>

        <div class="form-row">
          <div class="form-group col-6">
            <label class="small text-muted">Color</label>
            <div class="d-flex align-items-center">
              <input v-model="form.color" type="color" class="macro-color mr-2">
              <div class="macro-swatches">
                <button
                  v-for="preset in colorPresets"
                  :key="preset.value"
                  type="button"
                  class="macro-swatch-btn"
                  :class="{ 'is-active': form.color === preset.value }"
                  :style="{ background: preset.value }"
                  :title="preset.label"
                  @click="form.color = preset.value"
                />
              </div>
            </div>
          </div>
          <div class="form-group col-6">
            <label class="small text-muted">Icon</label>
            <select v-model="form.icon" class="form-control">
              <option v-for="opt in iconOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
        </div>

        <div class="custom-control custom-checkbox mb-3">
          <input id="macro-stop" type="checkbox" class="custom-control-input" v-model="form.stopOnError">
          <label class="custom-control-label" for="macro-stop">Stop on error</label>
        </div>

        <p class="small text-muted font-weight-bold mb-2">Steps</p>
        <div
          v-for="(step, sIdx) in form.steps"
          :key="sIdx"
          class="macro-step border rounded p-2 mb-2"
        >
          <div class="d-flex align-items-center mb-2">
            <span class="badge badge-secondary mr-2">{{ sIdx + 1 }}</span>
            <select v-model="step.kind" class="form-control form-control-sm" @change="onStepKindChange(sIdx)">
              <option v-for="k in stepKinds" :key="k.value" :value="k.value">{{ k.label }}</option>
            </select>
            <button type="button" class="btn btn-sm btn-link text-danger ml-1" @click="removeStep(sIdx)">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <template v-if="step.kind === 'command'">
            <select
              v-model="step.nodeId"
              class="form-control form-control-sm mb-1"
              @change="onCommandContextChange(sIdx)"
            >
              <option value="">-- Device --</option>
              <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
            </select>
            <div class="form-row">
              <div class="col-5">
                <select
                  v-model="step.adapter"
                  class="form-control form-control-sm mb-1"
                  @change="onAdapterChange(sIdx)"
                >
                  <option v-for="a in adapters" :key="a.value" :value="a.value">{{ a.label }}</option>
                </select>
              </div>
              <div class="col-7">
                <select
                  class="form-control form-control-sm mb-1"
                  :value="actionSelectValue(step)"
                  @change="onActionSelect(sIdx, $event.target.value)"
                >
                  <option value="">-- Action --</option>
                  <option v-for="a in actionsFor(step.adapter)" :key="a.value" :value="a.value">
                    {{ a.label }}
                  </option>
                  <option :value="customActionKey">Custom…</option>
                </select>
              </div>
            </div>
            <input
              v-if="isCustomCommand(step)"
              v-model="step.action"
              class="form-control form-control-sm mb-1"
              placeholder="Action name"
            >
            <div v-if="stepFetch(step)" class="mb-1">
              <div class="d-flex align-items-center mb-1">
                <input
                  v-if="stepFetch(step) === 'qlc-widget'"
                  :value="targetSearch(step)"
                  type="search"
                  class="form-control form-control-sm mr-1"
                  placeholder="Cari tombol…"
                  :disabled="!step.nodeId"
                  @input="setTargetSearch(sIdx, $event.target.value)"
                >
                <select
                  v-model="step.targetValue"
                  class="form-control form-control-sm mr-1"
                  :disabled="!step.nodeId"
                >
                  <option value="">{{ targetPlaceholder(step) }}</option>
                  <option
                    v-for="opt in filteredTargetOptions(step)"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary flex-shrink-0"
                  title="Refresh from node"
                  :disabled="!step.nodeId || targetBusy(step)"
                  @click="fetchStepTargets(sIdx, { force: true })"
                >
                  <i class="fas" :class="targetBusy(step) ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                </button>
              </div>
            </div>
            <p v-if="stepFetch(step) && !step.nodeId" class="small text-muted mb-1">Pilih device dulu untuk load opsi.</p>
            <p v-else-if="targetError(step)" class="text-danger small mb-1">{{ targetError(step) }}</p>
            <div v-if="stepFields(step).length" class="form-row">
              <div v-for="f in stepFields(step)" :key="f.key" class="col">
                <label class="small text-muted mb-0">{{ f.label }}</label>
                <select
                  v-if="f.type === 'boolean'"
                  class="form-control form-control-sm mb-1"
                  :value="(step.fieldValues && step.fieldValues[f.key]) ? '1' : '0'"
                  @change="setStepField(sIdx, f.key, $event.target.value === '1')"
                >
                  <option value="1">On</option>
                  <option value="0">Off</option>
                </select>
                <input
                  v-else
                  class="form-control form-control-sm mb-1"
                  type="number"
                  :min="f.min"
                  :max="f.max"
                  :value="step.fieldValues && step.fieldValues[f.key]"
                  @input="setStepField(sIdx, f.key, $event.target.value)"
                >
              </div>
            </div>
            <textarea
              v-if="isCustomCommand(step)"
              v-model="step.payloadText"
              class="form-control form-control-sm"
              rows="2"
              placeholder='Payload JSON (optional)'
            />
          </template>

          <template v-else-if="step.kind === 'alert'">
            <select v-model="step.nodeId" class="form-control form-control-sm mb-1">
              <option value="all">All devices</option>
              <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
            </select>
            <input
              v-model="step.message"
              class="form-control form-control-sm mb-1"
              maxlength="500"
              placeholder="Alert message"
            >
            <div class="form-row">
              <div class="col-6">
                <input v-model="step.color" type="color" class="macro-color">
              </div>
              <div class="col-6">
                <select v-model="step.icon" class="form-control form-control-sm">
                  <option v-for="opt in iconOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>
            </div>
          </template>

          <template v-else-if="step.kind === 'macro'">
            <select v-model.number="step.macroId" class="form-control form-control-sm">
              <option :value="null">-- QLC macro --</option>
              <option v-for="qm in qlcMacros" :key="qm.id" :value="qm.id">
                {{ qm.name }} ({{ qm.steps.length }} steps)
              </option>
            </select>
            <p v-if="!qlcMacros.length" class="small text-muted mb-0 mt-1">
              Belum ada QLC sequence.
              <router-link to="/qlc-macros">Buat di QLC Macros →</router-link>
            </p>
          </template>

          <template v-else-if="step.kind === 'delay'">
            <div class="input-group input-group-sm">
              <input v-model.number="step.delayMs" type="number" class="form-control" min="0" max="60000" step="100">
              <div class="input-group-append">
                <span class="input-group-text">ms</span>
              </div>
            </div>
          </template>
        </div>

        <button type="button" class="btn btn-sm btn-outline-secondary mb-3" @click="addStep">
          <i class="fas fa-plus"></i> Add step
        </button>

        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('edit-macro')">Cancel</button>
          <button class="btn btn-primary" :disabled="saving || !form.name.trim()" @click="save">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </modal>

    <modal name="delete-macro" height="auto" :width="400" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-2">Delete Macro</h5>
        <p class="text-muted mb-3">
          Hapus <strong>{{ deleteTarget && deleteTarget.name }}</strong>?
        </p>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('delete-macro')">Cancel</button>
          <button class="btn btn-danger" :disabled="deleting" @click="doDelete">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  MACRO_COLOR_PRESETS,
  MACRO_ICON_OPTIONS,
  MACRO_ADAPTERS,
  MACRO_STEP_KINDS,
  MACRO_CUSTOM_ACTION,
  blankMacro,
  blankMacroStep,
  macrosToForm,
  macrosFromForm,
  contrastText,
  macroActionsFor,
  findMacroAction,
  actionSelectValue,
  defaultFieldValues,
  parseCommandResult,
} from '@/widgets/macros'
import { getQlcButtons } from '@/widgets/qlcCache'

export default {
  data() {
    return {
      form: blankEditor(),
      deleteTarget: null,
      saving: false,
      deleting: false,
      runningId: null,
      runStatus: '',
      runOk: false,
      colorPresets: MACRO_COLOR_PRESETS,
      adapters: MACRO_ADAPTERS,
      stepKinds: MACRO_STEP_KINDS,
      customActionKey: MACRO_CUSTOM_ACTION,
      targetState: {},
      targetSearchByStep: {},
    }
  },
  computed: {
    ...mapGetters('macro', ['generalMacros', 'qlcMacros']),
    ...mapGetters('mission', ['nodes']),
    iconOptions() {
      return MACRO_ICON_OPTIONS.filter((o) => o.value !== 'lightbulb')
    },
  },
  async mounted() {
    await Promise.all([this.fetchMacros(), this.fetchNodes()])
  },
  methods: {
    ...mapActions('macro', ['fetchMacros', 'createMacro', 'updateMacro', 'deleteMacro', 'runMacro']),
    ...mapActions('mission', ['fetchNodes', 'sendCommand']),
    previewText(color) {
      return contrastText(color)
    },
    actionSelectValue,
    actionsFor(adapter) {
      return macroActionsFor(adapter)
    },
    isCustomCommand(step) {
      return !!step.customAction || (!!step.action && !findMacroAction(step.adapter, step.action))
    },
    stepDef(step) {
      if (!step || step.customAction) return null
      return findMacroAction(step.adapter, step.action)
    },
    stepFetch(step) {
      const def = this.stepDef(step)
      return def && def.fetch ? def.fetch : ''
    },
    stepFields(step) {
      const def = this.stepDef(step)
      return def && def.fields ? def.fields : []
    },
    targetCacheKey(step) {
      if (!step) return ''
      const fetch = this.stepFetch(step)
      if (!fetch || !step.nodeId) return ''
      return `${step.nodeId}:${step.adapter}:${fetch}`
    },
    targetPlaceholder(step) {
      const fetch = this.stepFetch(step)
      if (fetch === 'obs-scene') return '-- Scene --'
      if (fetch === 'qlc-widget') return '-- Button --'
      return '-- Select --'
    },
    targetBusy(step) {
      const st = this.targetState[this.targetCacheKey(step)]
      return !!(st && st.loading)
    },
    targetError(step) {
      const st = this.targetState[this.targetCacheKey(step)]
      return (st && st.error) || ''
    },
    targetOptions(step) {
      const st = this.targetState[this.targetCacheKey(step)]
      const opts = (st && st.options) || []
      const current = step.targetValue != null ? String(step.targetValue) : ''
      if (current && !opts.some((o) => String(o.value) === current)) {
        return [{ value: current, label: current }, ...opts]
      }
      return opts
    },
    targetSearch(step) {
      return this.targetSearchByStep[this.targetCacheKey(step)] || ''
    },
    setTargetSearch(idx, value) {
      const step = this.form.steps[idx]
      const key = this.targetCacheKey(step)
      if (!key) return
      this.$set(this.targetSearchByStep, key, value)
    },
    filteredTargetOptions(step) {
      const opts = this.targetOptions(step)
      if (this.stepFetch(step) !== 'qlc-widget') return opts
      const q = String(this.targetSearch(step) || '').trim().toLowerCase()
      if (!q) return opts
      return opts.filter((o) => {
        const label = String(o.label || '').toLowerCase()
        const value = String(o.value || '').toLowerCase()
        return label.includes(q) || value.includes(q)
      })
    },
    onAdapterChange(idx) {
      const step = this.form.steps[idx]
      const first = macroActionsFor(step.adapter)[0]
      step.action = first ? first.value : ''
      step.customAction = false
      step.targetValue = ''
      this.$set(step, 'fieldValues', defaultFieldValues(first || null))
      step.payloadText = ''
      this.fetchStepTargets(idx, { force: true })
    },
    onActionSelect(idx, value) {
      const step = this.form.steps[idx]
      if (value === MACRO_CUSTOM_ACTION) {
        this.$set(step, 'customAction', true)
        return
      }
      this.$set(step, 'customAction', false)
      step.action = value
      step.targetValue = ''
      this.$set(step, 'fieldValues', defaultFieldValues(findMacroAction(step.adapter, value)))
      this.fetchStepTargets(idx, { force: true })
    },
    onCommandContextChange(idx) {
      this.fetchStepTargets(idx, { force: true })
    },
    setStepField(idx, key, value) {
      const step = this.form.steps[idx]
      if (!step) return
      if (!step.fieldValues) this.$set(step, 'fieldValues', {})
      this.$set(step.fieldValues, key, value)
    },
    async fetchAllStepTargets() {
      await Promise.all(
        this.form.steps.map((_, idx) => this.fetchStepTargets(idx))
      )
    },
    async fetchStepTargets(idx, { force } = {}) {
      const step = this.form.steps[idx]
      if (!step || step.kind !== 'command') return
      const fetch = this.stepFetch(step)
      if (!fetch || !step.nodeId) return
      const key = this.targetCacheKey(step)
      const existing = this.targetState[key]
      if (!force && existing && existing.loaded) return
      this.$set(this.targetState, key, {
        loading: true,
        loaded: false,
        error: '',
        options: (existing && existing.options) || [],
      })
      try {
        this._forceTargetFetch = !!force
        const options = await this.loadTargetOptions(step, fetch)
        this._forceTargetFetch = false
        if (!this.form.steps[idx] || this.targetCacheKey(this.form.steps[idx]) !== key) return
        this.$set(this.targetState, key, { loading: false, loaded: true, error: '', options })
      } catch (e) {
        this._forceTargetFetch = false
        if (!this.form.steps[idx] || this.targetCacheKey(this.form.steps[idx]) !== key) return
        this.$set(this.targetState, key, {
          loading: false,
          loaded: false,
          error: e.response?.data?.message || e.message || 'Failed to load',
          options: [],
        })
      }
    },
    async loadTargetOptions(step, fetch) {
      if (fetch === 'obs-scene') {
        const res = await this.sendCommand({
          node_id: step.nodeId,
          adapter: 'obs',
          action: 'GetSceneList',
          payload: {},
        })
        const data = parseCommandResult(res) || {}
        const scenes = Array.isArray(data.scenes) ? data.scenes : []
        return scenes
          .map((s) => (typeof s === 'string' ? s : (s && s.sceneName) || ''))
          .filter(Boolean)
          .map((name) => ({ value: name, label: name }))
      }
      if (fetch === 'qlc-widget') {
        const widgets = await getQlcButtons(
          (payload) => this.sendCommand(payload),
          step.nodeId,
          { force: !!this._forceTargetFetch },
        )
        return widgets.map((w) => ({
          value: String(w.id),
          label: w.name || `Button ${w.id}`,
        }))
      }
      return []
    },
    openCreate() {
      this.form = blankEditor()
      this.$modal.show('edit-macro')
      this.$nextTick(() => this.fetchAllStepTargets())
    },
    openEdit(m) {
      const [form] = macrosToForm([{
        label: m.name,
        color: m.color,
        icon: m.icon,
        stopOnError: m.stopOnError,
        steps: m.steps,
      }])
      this.form = {
        id: m.id,
        name: m.name,
        color: form.color,
        icon: form.icon,
        stopOnError: form.stopOnError,
        steps: form.steps,
      }
      this.$modal.show('edit-macro')
      this.$nextTick(() => this.fetchAllStepTargets())
    },
    addStep() {
      this.form.steps.push(blankMacroStep('command'))
      this.$nextTick(() => this.fetchStepTargets(this.form.steps.length - 1))
    },
    removeStep(idx) {
      this.form.steps.splice(idx, 1)
    },
    onStepKindChange(idx) {
      const kind = this.form.steps[idx].kind
      this.$set(this.form.steps, idx, blankMacroStep(kind))
    },
    async save() {
      const name = this.form.name.trim()
      if (!name || this.saving) return
      const [normalized] = macrosFromForm([{
        label: name,
        color: this.form.color,
        icon: this.form.icon === 'lightbulb' ? 'play' : this.form.icon,
        stopOnError: this.form.stopOnError,
        steps: this.form.steps,
      }])
      if (!normalized) {
        this.$toast.error('Invalid macro')
        return
      }
      this.saving = true
      try {
        const payload = {
          name,
          color: normalized.color,
          icon: normalized.icon,
          stopOnError: normalized.stopOnError,
          steps: normalized.steps,
        }
        if (this.form.id) {
          await this.updateMacro({ id: this.form.id, ...payload })
          this.$toast.success('Macro updated')
        } else {
          await this.createMacro(payload)
          this.$toast.success('Macro created')
        }
        this.$modal.hide('edit-macro')
      } catch (e) {
        this.$toast.error(e.response?.data?.message || e.message || 'Failed to save')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(m) {
      this.deleteTarget = m
      this.$modal.show('delete-macro')
    },
    async doDelete() {
      if (!this.deleteTarget || this.deleting) return
      this.deleting = true
      try {
        await this.deleteMacro(this.deleteTarget.id)
        this.$toast.success('Macro deleted')
        this.$modal.hide('delete-macro')
        this.deleteTarget = null
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to delete')
      } finally {
        this.deleting = false
      }
    },
    async run(m) {
      if (this.runningId) return
      this.runningId = m.id
      this.runStatus = `Running “${m.name}”…`
      this.runOk = false
      try {
        const res = await this.runMacro(m)
        this.runOk = res.failed === 0
        this.runStatus = res.failed
          ? `Finished with errors — ${res.done} ok, ${res.failed} failed (${res.lastErr})`
          : `Done — ${res.done}/${res.total} steps`
      } catch (e) {
        this.runOk = false
        this.runStatus = e.message || 'Failed'
      } finally {
        this.runningId = null
      }
    },
  },
}

function blankEditor() {
  const b = blankMacro()
  return {
    id: null,
    name: '',
    color: b.color,
    icon: b.icon,
    stopOnError: true,
    steps: b.steps,
  }
}
</script>

<style scoped>
.macro-swatch {
  display: inline-flex;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}
.macro-color {
  width: 36px;
  height: 30px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}
.macro-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.macro-swatch-btn {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid transparent;
  padding: 0;
  cursor: pointer;
}
.macro-swatch-btn.is-active {
  border-color: #212529;
  box-shadow: 0 0 0 1px #fff inset;
}
.macro-step {
  background: #f8f9fa;
}
.macro-step .flex-shrink-0 {
  flex-shrink: 0;
}
.macro-modal {
  max-height: 80vh;
  overflow: auto;
}
</style>
